import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { BookingProvider } from "./contexts/BookingContext";
import { CustomerProvider } from "./contexts/CustomerContext";
import { CustomerAuthProvider, useCustomerAuth } from "./contexts/CustomerAuthContext";
import { Toaster } from "./components/ui/toaster";

// Public pages
import HomePage from "./components/HomePage";
import AboutPage from "./components/AboutPage";
import ExplorePage from "./components/ExplorePage";
import BookPage from "./components/BookPage";
import VenueDetail from "./components/VenueDetail";
import BookingFlow from "./components/BookingFlow";
import MyBookings from "./components/MyBookings";
import CustomerProfile from "./components/CustomerProfile";
import CommunityGamePublic from "./components/CommunityGamePublic";
import EventsPage from "./components/EventsPage";
import EventDetailPublic from "./components/EventDetailPublic";
import CoachingPage from "./components/CoachingPage";
import CoachingDetailPublic from "./components/CoachingDetailPublic";
import RefereePortal from "./components/RefereePortal";
import TournamentDetailPublic from "./components/TournamentDetailPublic";
import OTPLoginModal from "./components/OTPLoginModal";

// Layout
import TopHeader from "./components/TopHeader";
import BottomNav from "./components/BottomNav";

// Legacy public booking
import PublicBooking from "./components/PublicBooking";
import GridPreview from "./components/booking-grid/GridPreview";
import BookingGrid from "./components/booking-grid/BookingGrid";

// Crash-safety wrapper for booking surfaces
import ErrorBoundary from "./components/ErrorBoundary";

// Admin pages
import Login from "./components/Login";
import ForceChangePassword from "./components/ForceChangePassword";
import AdminDashboard from "./components/AdminDashboard";
import SuperAdminDashboard from "./components/SuperAdminDashboard";

// Global login modal renderer
function LoginModalRenderer() {
  const { showLoginModal, closeLoginModal } = useCustomerAuth();
  if (!showLoginModal) return null;
  return <OTPLoginModal onClose={closeLoginModal} />;
}

function AppContent() {
  return (
    <div className="App">
      <BrowserRouter>
        <TopHeader />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/join-a-game" element={<ExplorePage />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/venue/:venueId" element={<VenueDetail />} />
          <Route path="/book/:venueId" element={<ErrorBoundary label="BookingGrid"><BookingGrid /></ErrorBoundary>} />
          <Route path="/my-bookings" element={<MyBookings />} />
          <Route path="/profile" element={<CustomerProfile />} />
          <Route path="/community/:venueName/game/:gameId" element={<CommunityGamePublic />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:slug" element={<EventDetailPublic />} />
          <Route path="/coaching" element={<CoachingPage />} />
          <Route path="/coaching/:slug" element={<CoachingDetailPublic />} />
          <Route path="/tournaments/:slug" element={<TournamentDetailPublic />} />
          <Route path="/referee" element={<RefereePortal />} />
          
          {/* Legacy routes */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/book-legacy" element={<ErrorBoundary label="PublicBooking"><PublicBooking /></ErrorBoundary>} />
          <Route path="/book-legacy/:venueId" element={<ErrorBoundary label="BookingFlowLegacy"><BookingFlow /></ErrorBoundary>} />
          <Route path="/book-grid-preview" element={<ErrorBoundary label="GridPreview"><GridPreview /></ErrorBoundary>} />
          <Route path="/book-grid/:venueId" element={<ErrorBoundary label="BookingGrid"><BookingGrid /></ErrorBoundary>} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<Login />} />
          <Route path="/admin/change-password" element={<ForceChangePassword />} />
          <Route path="/superadmin/*" element={<SuperAdminDashboard />} />
          <Route path="/admin/*" element={<AdminDashboard />} />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <BottomNav />
        <LoginModalRenderer />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <CustomerProvider>
        <BookingProvider>
          <CustomerAuthProvider>
            <AppContent />
          </CustomerAuthProvider>
        </BookingProvider>
      </CustomerProvider>
    </AuthProvider>
  );
}

export default App;
